import { useMemo, useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  message,
  Modal,
  Row,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EllipsisOutlined,
} from '@ant-design/icons';
import type { CustomArea, SavedPolygons } from '../../../../types/domain';
import { useSavedPolygonData } from '../../../../hooks/data/useSavedPolygonData';
import { useCustomAreas } from '../../../../hooks/SimViewer/useCustomAreas';
import { useDataFilter } from '../../../../hooks/SimViewer/useDataFilter';
import { scrollbarStyle } from '../../../../styles/scrollbar';
import { normalizeJapanese } from '../../../../utils/case';

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const CustomAreaSelectModal = ({ isOpen, onClose }: Props): ReactNode => {
  const { t } = useTranslation();
  const { savedPolygons, isUpdating, updateSavedPolygon, deleteSavedPolygon } =
    useSavedPolygonData();
  const { customAreas, addCustomArea, deleteCustomArea } = useCustomAreas();
  const { deleteCustomAreaFilter } = useDataFilter();

  const [searchText, setSearchText] = useState<string>('');
  const [renamingCustomAreaId, setRenamingCustomAreaId] = useState<
    string | null
  >(null);
  const [newName, setNewName] = useState<string>('');
  const [isComposing, setIsComposing] = useState(false);

  const sortedCustomAreas = useMemo((): SavedPolygons[] => {
    return savedPolygons.toSorted(
      (customArea1: SavedPolygons, customArea2: SavedPolygons): number => {
        // 記号、数字、アルファベット（大文字と小文字の区別なし）、ひらがなとカタカナ（ひらがなとカタカナの区別なし）、漢字の順で並び替え
        return customArea1.name.localeCompare(customArea2.name, 'ja');
      }
    );
  }, [savedPolygons]);

  const searchedCustomAreas = useMemo((): SavedPolygons[] => {
    const normalizedSearchText = normalizeJapanese(searchText);

    return normalizedSearchText
      ? sortedCustomAreas.filter((customArea: SavedPolygons): boolean => {
          // 検索テキストを正規化して、カスタムエリアの名前に部分一致するかどうかを判定する
          return normalizeJapanese(customArea.name).includes(
            normalizedSearchText
          );
        })
      : sortedCustomAreas;
  }, [sortedCustomAreas, searchText]);

  const handleClose = (): void => {
    setSearchText('');
    setRenamingCustomAreaId(null);
    setNewName('');
    setIsComposing(false);
    onClose();
  };

  return (
    <StyledModal
      title={
        <div>
          <StyledHeaderTitle>{t('selectCustomArea')}</StyledHeaderTitle>
          <StyledSearchInput
            allowClear
            placeholder={t('searchCustomArea')}
            value={searchText}
            onChange={(event: ChangeEvent<HTMLInputElement>): void => {
              setSearchText(event.target.value);
            }}
          />
        </div>
      }
      open={isOpen}
      okText={t('done')}
      cancelButtonProps={{ style: { display: 'none' } }}
      onOk={handleClose}
      onCancel={handleClose}
      centered
      width={600}
    >
      <Row gutter={[32, 12]} align="middle" wrap>
        {searchedCustomAreas.length === 0 && (
          <Col span={24}>
            <Typography.Text type="secondary">
              {t('message.noCustomAreaFound')}
            </Typography.Text>
          </Col>
        )}
        {searchedCustomAreas.map(
          (savedCustomArea: SavedPolygons): ReactNode => {
            return (
              <Col
                key={savedCustomArea.saved_polygon_id}
                span={12}
                style={{ padding: '0 8px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'stretch',
                  }}
                >
                  <StyledCheckbox
                    checked={customAreas.some(
                      (customArea: CustomArea): boolean => {
                        return (
                          customArea.key === savedCustomArea.saved_polygon_id
                        );
                      }
                    )}
                    onChange={(event): void => {
                      if (event.target.checked) {
                        addCustomArea({
                          key: savedCustomArea.saved_polygon_id,
                          type: savedCustomArea.type,
                          label: savedCustomArea.name,
                          geometry: savedCustomArea.geometry,
                          radius: savedCustomArea.radius ?? 0,
                          minutes: savedCustomArea.minutes ?? 0,
                          center: savedCustomArea.center,
                        });
                      } else {
                        deleteCustomArea(savedCustomArea.saved_polygon_id);
                        deleteCustomAreaFilter(
                          savedCustomArea.saved_polygon_id,
                          'all'
                        );
                      }
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                      }}
                    >
                      {savedCustomArea.saved_polygon_id ===
                      renamingCustomAreaId ? (
                        <Input
                          type="text"
                          size="small"
                          value={newName}
                          maxLength={30}
                          autoFocus
                          style={{ flex: 1 }}
                          onChange={(
                            event: ChangeEvent<HTMLInputElement>
                          ): void => {
                            setNewName(event.target.value);
                          }}
                          onCompositionStart={(): void => {
                            setIsComposing(true);
                          }}
                          onCompositionEnd={(): void => {
                            setIsComposing(false);
                          }}
                          onPressEnter={(): void => {
                            if (!isComposing && newName.trim() !== '') {
                              updateSavedPolygon(
                                savedCustomArea.saved_polygon_id,
                                {
                                  name: newName,
                                }
                              ).then((): void => {
                                message.success(
                                  t('message.nameChanged', {
                                    from: savedCustomArea.name,
                                    to: newName,
                                  })
                                );
                              });
                              setRenamingCustomAreaId(null);
                              setNewName('');
                            }
                          }}
                          onBlur={(): void => {
                            setRenamingCustomAreaId(null);
                            setNewName('');
                          }}
                        />
                      ) : (
                        <Typography.Paragraph
                          ellipsis={{ rows: 2 }}
                          style={{ margin: 0, flex: 1 }}
                        >
                          {savedCustomArea.name}
                        </Typography.Paragraph>
                      )}
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: [
                            {
                              key: 'rename',
                              label: t('rename'),
                              icon: <EditOutlined />,
                              disabled: isUpdating,
                              onClick: (): void => {
                                if (
                                  savedCustomArea.saved_polygon_id ===
                                  renamingCustomAreaId
                                ) {
                                  if (
                                    newName.trim() !== '' &&
                                    newName !== savedCustomArea.name
                                  ) {
                                    updateSavedPolygon(
                                      savedCustomArea.saved_polygon_id,
                                      {
                                        name: newName,
                                      }
                                    ).then((): void => {
                                      message.success(
                                        t('message.nameChanged', {
                                          from: savedCustomArea.name,
                                          to: newName,
                                        })
                                      );
                                    });
                                  }
                                  setRenamingCustomAreaId(null);
                                  setNewName('');
                                } else {
                                  setRenamingCustomAreaId(
                                    savedCustomArea.saved_polygon_id
                                  );
                                  setNewName(savedCustomArea.name);
                                }
                              },
                            },
                            {
                              key: 'delete',
                              label: t('actions.delete'),
                              icon: <DeleteOutlined />,
                              disabled: isUpdating,
                              danger: true,
                              onClick: (): void => {
                                Modal.confirm({
                                  title: t('message.confirmDeletion', {
                                    name: savedCustomArea.name,
                                  }),
                                  okText: t('actions.delete'),
                                  cancelText: t('cancel'),
                                  okButtonProps: { danger: true },
                                  onOk: (): void => {
                                    deleteSavedPolygon(
                                      savedCustomArea.saved_polygon_id
                                    );
                                  },
                                });
                              },
                            },
                          ],
                        }}
                      >
                        <Button
                          type="text"
                          shape="default"
                          icon={<EllipsisOutlined />}
                        />
                      </Dropdown>
                    </div>
                  </StyledCheckbox>
                </div>
              </Col>
            );
          }
        )}
      </Row>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)`
  height: 80vh;
  .ant-modal-content {
    display: flex;
    flex-direction: column;
    height: 100%;
    .ant-modal-header {
      padding-bottom: 0;
    }
    .ant-modal-body {
      height: 100%;
      padding-inline: 32px;
      overflow-y: scroll;
      ${scrollbarStyle}
    }
  }
`;

const StyledHeaderTitle = styled.div`
  margin-block: 12px;
  color: rgba(0, 0, 0, 0.85);
  font-weight: 500;
  font-size: 16px;
  line-height: 22px;
  word-wrap: break-word;
`;

const StyledSearchInput = styled(Input)`
  margin-bottom: 12px;
`;

const StyledCheckbox = styled(Checkbox)`
  flex-grow: 1;
  align-items: center;

  :hover {
    background-color: rgba(0, 0, 0, 0.02);
  }

  > span:not(.ant-checkbox) {
    flex-grow: 1;
    padding-left: 4px;
  }

  .ant-checkbox {
    top: 0;
    margin: 8px;
  }
`;

export { CustomAreaSelectModal };
